"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";

import CsvUploader from "@/app/components/CsvUploader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { createNewSession, saveSession } from "@/lib/reviewState";
import { DistrictData } from "@/types/review";

export default function HomePage() {
  const [csvData, setCsvData] = useState<DistrictData[] | null>(null);
  const [error, setError] = useState<string>("");
  const router = useRouter();

  const handleDataLoaded = (data: DistrictData[]) => {
    setCsvData(data);
    setError("");

    // Store the CSV data in sessionStorage for the review page to access
    sessionStorage.setItem("csvData", JSON.stringify(data));

    // Create a new review session
    const session = createNewSession(data.length);
    saveSession(session);
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setCsvData(null);
  };

  const startReview = () => {
    if (csvData) {
      router.push("/review");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            URL Review Tool
          </h1>
          <p className="text-xl text-gray-600">
            Upload your district data CSV to begin reviewing job board URLs
          </p>
        </div>

        <div className="space-y-6">
          {/* Error Display */}
          {error && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <p className="text-red-800 text-center">{error}</p>
              </CardContent>
            </Card>
          )}

          {/* CSV Upload Section */}
          <CsvUploader onDataLoaded={handleDataLoaded} onError={handleError} />

          {/* Success State */}
          {csvData && (
            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-green-800">
                  CSV Loaded Successfully!
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-700">
                        {csvData.length}
                      </div>
                      <div className="text-green-600">Districts Found</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-700">
                        {
                          csvData.filter(
                            (d) =>
                              d.current_ats_url && d.current_ats_url.trim(),
                          ).length
                        }
                      </div>
                      <div className="text-green-600">With URLs</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-700">
                        {new Set(csvData.map((d) => d.state_abbreviation)).size}
                      </div>
                      <div className="text-green-600">States</div>
                    </div>
                  </div>

                  <Button onClick={startReview} className="w-full" size="lg">
                    Start Reviewing URLs
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>How It Works</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-2">1</div>
                  <h3 className="font-medium mb-1">Upload CSV</h3>
                  <p className="text-sm text-gray-600">
                    Upload your district data CSV file with the required columns
                  </p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-2">2</div>
                  <h3 className="font-medium mb-1">Review URLs</h3>
                  <p className="text-sm text-gray-600">
                    Check each job board URL using the split-screen interface
                  </p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-2">3</div>
                  <h3 className="font-medium mb-1">Mark Status</h3>
                  <p className="text-sm text-gray-600">
                    Use keyboard shortcuts (C/I/S) to quickly mark URL status
                  </p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-2">4</div>
                  <h3 className="font-medium mb-1">Export Results</h3>
                  <p className="text-sm text-gray-600">
                    Download your review results as CSV for further processing
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
